import {
	Navigation,
	NavigationCallback,
	createBrowserNavigationUrl
} from "client/Navigation";
import { navigateToComposeAppointmentInOwa } from "../Email/OwaNavigation/owaNavigator";
import {
	DisabledCalendarClient,
	ICalendarClientCreateFeature,
	ICalendarClientDetailsFeature,
	ICalendarClientFeatures,
	ICalendarClientOverviewFeature,
	OpenOverviewOptions,
	OwaCalendarClient
} from "./calendarClient";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { calendarRouting } from "./CalendarPage";
import { ReactElement, useMemo } from "react";
import { useAppointmentDetailsDialog } from "./AppointmentDetails/AppointmentDetailsDialog";
import { CalendarDestinationApp, DestinationWindow } from "client/Api/Apps/common";
import { isNil } from "client/Misc/utils";
import {
	CalendarClientPreference,
	CalendarClientConfiguration,
	userConfiguration
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { never } from "client_legacy/ndaw/errors";
import { useValueEqualMemo } from "client/Hooks";
import { AppointmentOrIdentifier } from "./common";
import { AppointmentIdentifier } from "client/Api/Calendar/calendar";

class ReactWorkspaceCalendarClient implements
	ICalendarClientCreateFeature,
	ICalendarClientOverviewFeature,
	ICalendarClientDetailsFeature,
	ICalendarClientFeatures
{
	private readonly _shouldOpenInNewWindowByDefault: boolean;
	private readonly _onOpenCalendarDetails: (appointmentOrIdentifier: AppointmentOrIdentifier) => void;
	private readonly _onNavigate: NavigationCallback | undefined;

	public get doesSupportCreation(): true { return true; }
	public get doesSupportOverview(): true { return true; }
	public get doesSupportDetails(): true { return true; }
	public get isExternal(): false { return false; }

	constructor(
		shouldOpenInNewWindowByDefault: boolean,
		onOpenCalendarDetails: (appointmentOrIdentifier: AppointmentOrIdentifier) => void,
		onNavigate?: NavigationCallback
	) {
		this._shouldOpenInNewWindowByDefault = shouldOpenInNewWindowByDefault;
		this._onOpenCalendarDetails = onOpenCalendarDetails;
		this._onNavigate = onNavigate;
	}

	public createAppointment(): void {
		navigateToComposeAppointmentInOwa();
	}

	public openOverview(options: OpenOverviewOptions = {}): void {
		const {
			isInNewWindow = this._shouldOpenInNewWindowByDefault,
			date
		} = options;

		const navigation: Navigation = calendarRouting.page({
			queryParams: { date: date?.toISOString() }
		});

		if (!this._onNavigate || isInNewWindow) {
			this.navigateWithBrowser(navigation, isInNewWindow);
		} else {
			this._onNavigate(navigation);
		}
	}

	public openDetails(options: { isInNewWindow?: boolean | undefined; appointmentIdentifier: AppointmentIdentifier; }): void {
		const {
			appointmentIdentifier
		} = options;

		this._onOpenCalendarDetails(appointmentIdentifier);
	}

	private navigateWithBrowser(navigation: Navigation | string, isInNewWindow: boolean): void {
		const url: string = createBrowserNavigationUrl(navigation);

		navigateTo(url, isInNewWindow);
	}
}

export type EnabledReactCalendarClient =
	| ReactWorkspaceCalendarClient
	| OwaCalendarClient;

export type ReactCalendarClient =
	| DisabledCalendarClient
	| EnabledReactCalendarClient;

export type CalendarAppConfiguration = {
	destinationApp: CalendarDestinationApp;
	destinationWindow: DestinationWindow;
};

type CommonCalendarClientOptions = {
	onNavigate?: NavigationCallback;
};

type SpecificCalendarClientOptions = {
	appConfiguration: CalendarAppConfiguration;
} & CommonCalendarClientOptions;

type GlobalCalendarClientOptions = {
	appConfiguration?: undefined;
} & CommonCalendarClientOptions;

export type CalendarClientOptions =
	| SpecificCalendarClientOptions
	| GlobalCalendarClientOptions;

export function useCalendarClient(options: SpecificCalendarClientOptions): [
	element: ReactElement | null,
	client: EnabledReactCalendarClient
];
export function useCalendarClient(options: GlobalCalendarClientOptions): [
	element: ReactElement | null,
	client: ReactCalendarClient
];
export function useCalendarClient(options: CalendarClientOptions): [
	element: ReactElement | null,
	client: ReactCalendarClient
] {
	const {
		appConfiguration: appConfigurationFromProps,
		onNavigate
	} = options;

	const appConfiguration = useValueEqualMemo(
		(): CalendarAppConfiguration | undefined => appConfigurationFromProps,
		[appConfigurationFromProps]
	);

	const [appointmentDetailsDialog, openAppointmentDetailsDialog] = useAppointmentDetailsDialog();

	const renderElement = (): ReactElement | null => {
		if (!appointmentDetailsDialog) {
			return null;
		}

		return (
			<>
				{appointmentDetailsDialog}
			</>
		);
	};

	const globalCalendarConfiguration = useMemo(
		(): CalendarClientConfiguration => {
			return userConfiguration()
				.calendar;
		},
		[]
	);

	const client = useMemo(
		(): ReactCalendarClient => {
			let resolvedAppConfiguration: CalendarAppConfiguration;

			if (isNil(appConfiguration)) {
				switch (globalCalendarConfiguration.clientPreference) {
					case CalendarClientPreference.UseNone:
						return new DisabledCalendarClient();
					case CalendarClientPreference.Workspace:
						resolvedAppConfiguration = {
							destinationApp: CalendarDestinationApp.Workspace,
							destinationWindow: globalCalendarConfiguration.destinationWindow
						};
						break;
					case CalendarClientPreference.OutlookWebApp:
						resolvedAppConfiguration = {
							destinationApp: CalendarDestinationApp.OutlookWebApp,
							destinationWindow: globalCalendarConfiguration.destinationWindow
						};
						break;
					default:
						never(globalCalendarConfiguration.clientPreference);
				}
			} else {
				resolvedAppConfiguration = appConfiguration;
			}

			switch (resolvedAppConfiguration.destinationApp) {
				case CalendarDestinationApp.Workspace:
					return new ReactWorkspaceCalendarClient(
						resolvedAppConfiguration.destinationWindow === DestinationWindow.NewWindow,
						(appointmentOrIdentifier: AppointmentOrIdentifier): void => openAppointmentDetailsDialog({ appointmentOrIdentifier }),
						onNavigate
					);
				case CalendarDestinationApp.OutlookWebApp:
					return new OwaCalendarClient(resolvedAppConfiguration.destinationWindow === DestinationWindow.NewWindow);
				default:
					never(resolvedAppConfiguration.destinationApp);
			}
		},
		[
			appConfiguration,
			globalCalendarConfiguration,
			onNavigate,
			openAppointmentDetailsDialog
		]
	);

	return [
		renderElement(),
		client
	];
}